import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import {
  isUserRolePatientSelector,
  isUserRoleProviderSelector,
  isUserRoleRepresentativeSelector,
} from '@core/store/selectors/user-role.selectors';
import { select, Store } from '@ngrx/store';
import {
  ToolBarModule,
  ToolbarUserDialogComponent,
  ToolBarUserMenu,
  UserDialogData,
} from '@xt/ui-kit';
import { UserInfoService } from '@core/services/user-info.service';
import { AuthService } from '@core/services/auth.service';
import { CommonModule } from '@angular/common';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { ButtonModule } from '@shared/components/atoms/button/button.module';
import { MatIconModule } from '@angular/material/icon';
import { AvatarModule } from '@shared/components/atoms/avatar/avatar.module';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslocoModule } from '@jsverse/transloco';
import { NotificationIndicatorComponent } from '@shared/components/dashboard/notification-indicator/notification-indicator.component';

@Component({
  selector: 'xt-portal-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CypressDirectiveModule,
    ButtonModule,
    MatIconModule,
    AvatarModule,
    MatToolbarModule,
    MatDialogModule,
    ToolBarModule,
    TranslocoModule,
    RouterModule,
    NotificationIndicatorComponent,
  ],
})
export class HeaderComponent {
  private router = inject(Router);
  public dialog = inject(MatDialog);
  public store = inject(Store);
  private userInfoService = inject(UserInfoService);
  private authService = inject(AuthService);

  @Input() isProfileActive: boolean;
  @Input() isUserRolePatient: boolean;
  @Input() isUserRoleProvider: boolean;
  @Input() emergencyButtonUrl: string;
  @Input() partnerLogoUrl: string;
  @Input() acronym: string;
  @Input() name: string;
  @Input() email: string;
  @Input() hideSidenavMenu = false;

  @Output() openSidenavEvent = new EventEmitter();
  @Output() toggleMenuEvent = new EventEmitter();

  isUserRolePatient$: Observable<boolean>;
  isUserRoleProvider$: Observable<boolean>;
  isUserRoleRepresentative$: Observable<boolean>;

  isOnboardingLevelFull$: Observable<boolean> =
    this.userInfoService.isOnboardingLevelFull$;

  links: ToolBarUserMenu[];

  constructor() {
    this.isUserRolePatient$ = this.store.pipe(
      select(isUserRolePatientSelector),
    );
    this.isUserRoleProvider$ = this.store.pipe(
      select(isUserRoleProviderSelector),
    );
    this.isUserRoleRepresentative$ = this.store.pipe(
      select(isUserRoleRepresentativeSelector),
    );
    this.links = [
      {
        url: '/dashboard/profile',
        dataCy: 'dashboard-user-dialog.profile.navigate',
        displayName: 'app.profileQuickMenu.profileSettings',
        visible: this.userInfoService.hasVerifiedRole$,
      },
      {
        url: '/dashboard/logbook',
        dataCy: 'dashboard-user-dialog.logbook.navigate',
        displayName: 'app.profileQuickMenu.logbook',
        visible: this.isUserRolePatient$,
      },
      // {
      //   url: '/dashboard/privacy-cockpit',
      //   dataCy: 'dashboard-user-dialog.privacy-cockpit.navigate',
      //   displayName: 'app.profileQuickMenu.privacyCockpit',
      //   visible: this.isUserRolePatient$,
      // },
      {
        url: '/dashboard/privacy-policy',
        dataCy: 'dashboard-user-dialog.privacy-policy.navigate',
        displayName: 'app.profileQuickMenu.privacyPolicy',
        visible: this.userInfoService.hasVerifiedRole$,
      },
      {
        url: '/dashboard/verification',
        dataCy: 'dashboard-user-dialog.verifier-portal.navigate',
        displayName: 'app.profileQuickMenu.verifierPortal',
        visible: this.isUserRoleProvider$,
      },
    ];
  }

  openSidenav() {
    this.openSidenavEvent.emit();
  }

  toggleMenu() {
    this.toggleMenuEvent.emit();
  }

  // opens the dialog for the user profile
  openUserDialog() {
    this.toggleMenu();

    const dialogRef = this.dialog.open<
      ToolbarUserDialogComponent,
      UserDialogData
    >(ToolbarUserDialogComponent, {
      width: '250px',
      panelClass: 'dialog-container-user',
      backdropClass: 'backdrop-no-shadow',
      autoFocus: false,
      data: {
        user: {
          acronym: this.acronym,
          name: this.name,
          email: this.email,
        },
        userMenu: this.links,
      },
    });

    if (dialogRef && dialogRef.componentInstance) {
      dialogRef.componentInstance.logout.subscribe(() => {
        this.authService.logoutOAuth();
      });
    }

    this.router.events.subscribe(() => {
      dialogRef.close();
    });
  }
}
